import { Button, Modal, Progress, Select, Space, Typography } from 'antd';
import recruitmentAiApi, { AICVMatch } from 'api/recruitmentAiApi';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AiOutputLanguageSelector, {
  AiOutputLanguage,
  getDefaultAiOutputLanguage,
} from 'features/recruitment/components/AiOutputLanguageSelector';

const { Paragraph, Text, Title } = Typography;

interface CandidateAiAnalysisModalProps {
  visible: boolean;
  data: any;
  onClose: () => void;
  onSave: (analysis: AICVMatch) => Promise<void> | void;
  onReject: (analysis: AICVMatch) => Promise<void> | void;
}

const CandidateAiAnalysisModal = ({
  visible,
  data,
  onClose,
  onSave,
  onReject,
}: CandidateAiAnalysisModalProps) => {
  const { t, i18n } = useTranslation(['recruitment', 'action']);
  const [selectedJobId, setSelectedJobId] = useState<string>();
  const [analysis, setAnalysis] = useState<AICVMatch | null>(null);
  const [outputLanguage, setOutputLanguage] = useState<AiOutputLanguage>(() =>
    getDefaultAiOutputLanguage(i18n.language),
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const jobs = useMemo(() => data?.jobs || data?.jobApplied || [], [data]);

  useEffect(() => {
    if (!visible) return;
    setAnalysis(null);
    setSelectedJobId(jobs?.[0]?.id || jobs?.[0]?.candidateRequisitionId);
  }, [jobs, visible]);

  const selectedJob = jobs.find((job) => (job.id || job.candidateRequisitionId) === selectedJobId);

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const result = await recruitmentAiApi.analyzeCVMatch({
        context: {
          candidateId: data?.id,
          cvUrl: data?.linkProfile,
          candidate: {
            candidateName: data?.candidateName,
            currentJob: data?.currentJob,
            yearExperience: data?.yearExperience,
            skills: data?.skills,
            certifications: data?.certifications,
            education: data?.education,
            languages: data?.languages,
          },
          job: selectedJob,
        },
        options: { language: outputLanguage },
      });
      setAnalysis({
        ...result,
        candidateProcessId: selectedJob?.candidateProcessId,
        candidateRequisitionId: selectedJob?.jobCandidateId,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      await onSave(analysis);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const confirmReject = () => {
    if (!analysis) return;
    Modal.confirm({
      title: t('recruitment:ai_confirm_reject_title'),
      content: t('recruitment:ai_confirm_reject_description'),
      okText: t('recruitment:ai_reject_candidate'),
      cancelText: t('action:cancel'),
      okType: 'danger',
      onOk: () => onReject(analysis),
    });
  };

  return (
    <Modal
      visible={visible}
      width={760}
      centered
      maskClosable={false}
      title={t('recruitment:ai_cv_match_title')}
      onCancel={loading || saving ? undefined : onClose}
      footer={
        analysis
          ? [
              <Button key="close" disabled={saving} onClick={onClose}>
                {t('action:cancel')}
              </Button>,
              <Button key="save" loading={saving} onClick={handleSave}>
                {t('recruitment:ai_save_to_notes')}
              </Button>,
              ...(analysis.recommendedAction === 'reject'
                ? [
                    <Button key="reject" danger disabled={saving} onClick={confirmReject}>
                      {t('recruitment:ai_reject_candidate')}
                    </Button>,
                  ]
                : []),
            ]
          : null
      }
    >
      {!analysis && (
        <>
          <Paragraph type="secondary">{t('recruitment:ai_cv_match_description')}</Paragraph>
          <LanguageRow>
            <AiOutputLanguageSelector
              value={outputLanguage}
              onChange={setOutputLanguage}
              disabled={loading}
            />
          </LanguageRow>
          {jobs.length > 0 && (
            <Field>
              <Text>{t('recruitment:select_applied_job')}</Text>
              <Select style={{ width: '100%' }} value={selectedJobId} onChange={setSelectedJobId}>
                {jobs.map((job) => (
                  <Select.Option
                    key={job.id || job.candidateRequisitionId}
                    value={job.id || job.candidateRequisitionId}
                  >
                    {job.positionName || job.name}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          )}
          <ActionRow>
            <PrimaryButton loading={loading} onClick={handleAnalyze}>
              {loading ? t('recruitment:ai_analyzing') : t('recruitment:ai_analyze_cv_jd')}
            </PrimaryButton>
          </ActionRow>
        </>
      )}

      {analysis && (
        <ResultLayout>
          <ScoreColumn>
            <Progress
              type="circle"
              percent={Math.max(0, Math.min(100, analysis.matchScore))}
              strokeColor="#f1592a"
            />
            <Text>{t('recruitment:ai_match_score')}</Text>
          </ScoreColumn>
          <ResultContent>
            <Title level={5}>{t('recruitment:ai_summary')}</Title>
            <Paragraph>{analysis.summary}</Paragraph>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <ListSection>
                <Text strong>{t('recruitment:ai_strengths')}</Text>
                <ul>
                  {analysis.strengths.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </ListSection>
              <ListSection>
                <Text strong>{t('recruitment:ai_skill_gaps')}</Text>
                <ul>
                  {analysis.gaps.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </ListSection>
            </Space>
          </ResultContent>
        </ResultLayout>
      )}
    </Modal>
  );
};

export default CandidateAiAnalysisModal;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`;

const LanguageRow = styled.div`
  margin-bottom: 20px;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const PrimaryButton = styled(Button)`
  color: var(--cl_white);
  border-color: var(--cl_primary500);
  background-color: var(--cl_primary500);

  &:hover,
  &:focus {
    color: var(--cl_white);
    border-color: var(--cl_primary500);
    background-color: var(--cl_primary500);
  }
`;

const ResultLayout = styled.div`
  display: flex;
  gap: 28px;
`;

const ScoreColumn = styled.div`
  display: flex;
  flex: 0 0 130px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultContent = styled.div`
  flex: 1;
`;

const ListSection = styled.div`
  ul {
    padding-left: 20px;
    margin: 8px 0 0;
  }
`;
